/**
 * WhatsAppCard — привязка номера WhatsApp Cloud API к клинике: ID номера
 * в Meta (по нему вебхук находит клинику), сам номер, WABA для шаблонов,
 * режим номера и приём сообщений. Секреты Meta живут в окружении сервиса.
 *
 * Поля заполняем текущей привязкой из карточки клиента. Раньше бэк её не
 * отдавал, и форма открывалась пустой — а ручка `PUT` перезаписывает
 * привязку целиком. То есть «поправить одну цифру в номере» означало
 * вбить заново всё, и забытый WABA молча обнулялся. Теперь правим то,
 * что есть.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {connectWhatsApp, type WhatsAppBinding, type WhatsAppMode} from '@services/clients';
import {keys} from '@queries/keys';
import {Field, TextInput} from '@components/FormKit';
import {Button} from '@components/ui/Button';
import {cn} from '@lib/utils';

import {SectionCard} from '../../SectionCard';

const MODES: {key: WhatsAppMode; label: string}[] = [
  {key: 'api_only', label: 'Отдельный номер'},
  {key: 'coexistence', label: 'Рабочий номер клиники'},
];

type Props = {
  clientId: string;
  /** Текущая привязка; null — номер ещё не подключён. */
  binding: WhatsAppBinding | null;
  onSaved: (text: string) => void;
};

export const WhatsAppCard = ({clientId, binding, onSaved}: Props) => {
  const queryClient = useQueryClient();

  const [phoneNumberId, setPhoneNumberId] = useState(binding?.phoneNumberId ?? '');
  const [displayPhone, setDisplayPhone] = useState(binding?.displayPhone ?? '');
  const [wabaId, setWabaId] = useState(binding?.wabaId ?? '');
  const [mode, setMode] = useState<WhatsAppMode>(binding?.mode ?? 'api_only');
  const [active, setActive] = useState(binding?.active ?? true);

  const mutation = useMutation({
    mutationFn: connectWhatsApp,
    onSuccess: (b) => {
      void queryClient.invalidateQueries({queryKey: keys.clients.detail(clientId)});
      onSaved(b.active ? `Номер ${b.displayPhone} привязан` : 'Приём сообщений выключен');
    },
  });

  const valid = phoneNumberId.trim() !== '' && displayPhone.replace(/\D/g, '').length >= 9;
  const handleSave = () =>
    mutation.mutate({
      id: clientId,
      phoneNumberId,
      displayPhone: `+${displayPhone.replace(/\D/g, '')}`,
      wabaId: wabaId.trim() === '' ? null : wabaId,
      mode,
      active,
    });

  return (
    <SectionCard
      title="WhatsApp"
      hint={
        binding === null
          ? 'Номер ещё не подключён — заполните поля и сохраните.'
          : 'Сохранение перезаписывает привязку целиком, а не отдельные поля.'
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="ID номера в Meta (phone_number_id)">
          <TextInput
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            placeholder="109876543210987"
            mono
          />
        </Field>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Номер">
            <TextInput
              value={displayPhone}
              onChange={(e) => setDisplayPhone(e.target.value)}
              placeholder="+996 555 123 456"
              inputMode="tel"
              mono
            />
          </Field>
          <Field label="WABA ID (для шаблонов)">
            <TextInput
              value={wabaId}
              onChange={(e) => setWabaId(e.target.value)}
              placeholder="необязательно"
              mono
            />
          </Field>
        </div>
        <div className="flex rounded-[10px] border border-skr-line p-[3px]">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={cn(
                'flex-1 rounded-[8px] py-1.5 text-[12.5px] font-medium text-muted-foreground transition',
                mode === m.key && 'bg-skr-head font-semibold text-foreground',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-[13px] text-skr-ink2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="size-4 accent-[hsl(var(--primary))]"
          />
          Принимать сообщения (выключение сохраняет переписку)
        </label>
        {mutation.isError && (
          <div className="rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
            Не удалось сохранить привязку. Проверьте ID номера и попробуйте ещё раз
          </div>
        )}
        <Button className="h-10" disabled={!valid || mutation.isPending} onClick={handleSave}>
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить привязку'}
        </Button>
      </div>
    </SectionCard>
  );
};
